import { FC } from 'react';
import { Box, Button, Typography, Card, CardContent, LinearProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { posts } from '../services/posts';
import { Post } from '../types/Post';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface SeriesNavigationProps {
  currentPost: Post;
}

export const SeriesNavigation: FC<SeriesNavigationProps> = ({ currentPost }) => {
  if (!currentPost.series) return null;

  const seriesPosts = posts
    .filter(post => post.series?.name === currentPost.series.name)
    .sort((a, b) => a.series.order - b.series.order);

  const currentIndex = seriesPosts.findIndex(post => post.slug === currentPost.slug);
  const prevPost = currentIndex > 0 ? seriesPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < seriesPosts.length - 1 ? seriesPosts[currentIndex + 1] : null;
  const progress = ((currentIndex + 1) / seriesPosts.length) * 100;

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom>
            {currentPost.series.name}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Part {currentPost.series.order} of {seriesPosts.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{ 
              height: 8,
              borderRadius: 1,
              backgroundColor: 'primary.lighter',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main'
              }
            }}
          />
        </Box>
        
        {/* Series Table of Contents */}
        <Box sx={{ mb: 3 }}>
          {seriesPosts.map((post) => (
            <Typography
              key={post.slug}
              component={RouterLink}
              to={`/blog/post/${post.slug}`}
              sx={{
                display: 'block',
                color: post.slug === currentPost.slug ? 'primary.main' : 'text.secondary',
                textDecoration: 'none',
                fontWeight: post.slug === currentPost.slug ? 'bold' : 'normal',
                py: 0.5,
                '&:hover': { color: 'primary.main' }
              }}
            >
              {post.series.order}. {post.title}
            </Typography>
          ))}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {prevPost && (
            <Button
              component={RouterLink}
              to={`/blog/post/${prevPost.slug}`}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
            >
              Previous: {prevPost.title}
            </Button>
          )}
          {nextPost && (
            <Button
              component={RouterLink}
              to={`/blog/post/${nextPost.slug}`}
              endIcon={<ArrowForwardIcon />}
              variant="contained"
              sx={{ ml: 'auto' }}
            >
              Next: {nextPost.title}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}; 